// skipcq: JS-W1028
import { useContext, useEffect, useMemo, useRef } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { Link as RouterLink, useOutletContext, useParams } from 'react-router-dom';

import PintPortfolioBeerProfile from '../../components/pintPortfolio/PintPortfolioBeerProfile';
import { BrewhouseBeerCatalogContext } from '../../context/BrewhouseBeerCatalogContext';
import { BrewerySessionContext } from '../../context/BrewerySessionContext';
import { trackPintPortfolioViewBeerProfile } from '../../utils/brewhousePortalAnalytics';

function findBeerInCatalog(beers, beerId) {
  if (!Array.isArray(beers) || typeof beerId !== 'string' || beerId.length === 0) {
    return null;
  }
  return beers.find((beer) => beer.id === beerId) || null;
}

function PintPortfolioBeerProfilePage() {
  const { beerId = '' } = useParams();
  const outletContext = useOutletContext() || {};
  const {
    breweryId,
    gridBeers = [],
    catalogLoading = false,
    styleMetaMap = {},
  } = outletContext;

  const { locationFilteredBeers = [], displayBeers = [], breweryNameById = {} } =
    useContext(BrewhouseBeerCatalogContext);
  const { activeBreweryName = '', breweryGroupId = '' } = useContext(BrewerySessionContext);

  const trackedBeerRef = useRef('');

  const beer = useMemo(() => {
    const candidates = [
      findBeerInCatalog(gridBeers, beerId),
      findBeerInCatalog(locationFilteredBeers, beerId),
      findBeerInCatalog(displayBeers, beerId),
    ];
    return candidates.find((candidate) => candidate !== null) || null;
  }, [beerId, gridBeers, locationFilteredBeers, displayBeers]);

  useEffect(() => {
    if (!breweryId || !beer?.id || trackedBeerRef.current === beer.id) {
      return;
    }
    trackedBeerRef.current = beer.id;
    trackPintPortfolioViewBeerProfile({ breweryId, beerId: beer.id });
  }, [breweryId, beer?.id]);

  if (catalogLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (!beer) {
    return (
      <Box sx={{ py: 3 }}>
        <Typography variant="h6" component="h2" gutterBottom>
          Beer not found
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This beer is not in your current portfolio or may have been removed.
        </Typography>
        <Button component={RouterLink} to="/brewery-portal/pint-portfolio" variant="outlined">
          Back to Pint Portfolio
        </Button>
      </Box>
    );
  }

  return (
    <PintPortfolioBeerProfile
      beer={beer}
      breweryId={breweryId}
      breweryName={activeBreweryName}
      breweryGroupId={breweryGroupId || null}
      styleMetaMap={styleMetaMap}
      breweryNameById={breweryNameById}
    />
  );
}

export default PintPortfolioBeerProfilePage;
